import { apiGet, apiGetResponse, apiPost, apiDelete } from '../lib/api-client';

export interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  parentId?: number;
  sortOrder: number;
  isActive: boolean;
}

export interface ProductImage {
  id: number;
  productId: number;
  url: string;
  alt: string;
  sortOrder: number;
  isFeatured: boolean;
}

export interface ProductVariant {
  id: number;
  productId: number;
  name: string;
  price?: number;
  stock?: number;
  sku?: string;
  imageId?: number;
  sortOrder: number;
}

export interface Product {
  id: number;
  name: string;
  slug: string;
  sku: string;
  categoryId: number;
  description: string;
  shortDescription: string;
  price: number;
  comparePrice?: number;
  stock: number;
  lowStockThreshold: number;
  isActive: boolean;
  isFeatured: boolean;
  isDigital: boolean;
  warranty?: string;
  specifications?: Record<string, string | number | boolean | string[]>;
  averageRating: number;
  reviewCount: number;
  createdAt: string;
  images: ProductImage[];
  variants?: ProductVariant[];
}

export interface ProductReviews {
  id: number;
  productId: number;
  userId: number;
  rating: number;
  title?: string;
  content: string;
  isVerified: boolean;
  isApproved: boolean;
  helpful: number;
  createdAt: string;
  user?: { name: string };
}

export interface ProductListResponse {
  success: boolean;
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ProductDetailResponse {
  success: boolean;
  data: {
    product: Product;
    images: ProductImage[];
    variants: ProductVariant[];
    reviews: ProductReviews[];
    related: Product[];
  };
}

export const productService = {
  async getCategories(): Promise<ProductCategory[]> {
    return apiGet<ProductCategory[]>('/products/categories');
  },

  async getProducts(params?: {
    category?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    sort?: 'price_asc' | 'price_desc' | 'newest' | 'popular' | 'rating';
    page?: number;
    limit?: number;
  }): Promise<ProductListResponse> {
    const query = new URLSearchParams();
    if (params?.category) query.append('category', params.category);
    if (params?.search) query.append('search', params.search);
    if (params?.minPrice) query.append('minPrice', String(params.minPrice));
    if (params?.maxPrice) query.append('maxPrice', String(params.maxPrice));
    if (params?.sort) query.append('sort', params.sort);
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    // The API returns `{ success, data: Product[], pagination }` at the top level;
    // use the raw response so callers get both the items and pagination info.
    return apiGetResponse<ProductListResponse>(`/products?${query.toString()}`);
  },

  async getProductBySlug(slug: string): Promise<ProductDetailResponse['data']> {
    return apiGet<ProductDetailResponse['data']>(`/products/${slug}`);
  },

  async getReviews(productId: number): Promise<ProductReviews[]> {
    return apiGet<ProductReviews[]>(`/products/${productId}/reviews`);
  },

  async addToWishlist(productId: number): Promise<void> {
    await apiPost('/products/wishlist', { productId });
  },

  async removeFromWishlist(productId: number): Promise<void> {
    await apiDelete(`/products/wishlist/${productId}`);
  },

  async getWishlist(): Promise<Product[]> {
    return apiGet<Product[]>('/products/wishlist');
  },

  async checkWishlist(productId: number): Promise<{ inWishlist: boolean }> {
    return apiGet<{ inWishlist: boolean }>(`/products/wishlist/check/${productId}`);
  },
};